/**
 * US / UK tax + payroll conveniences (owner-approved scope 2026-11-03). Pure constants and helpers shared by the API,
 * the web app and the tests. Nothing here applies to shops in other countries: every caller gates on taxRegimeOf().
 */

export type TaxRegime = "us" | "gb";
/** Which tax/payroll conveniences a shop (or branch) sees: US sales tax, UK VAT — nothing elsewhere. */
export function taxRegimeOf(country: string | null | undefined): TaxRegime | null {
  const c = String(country ?? "").trim().toUpperCase();
  return c === "US" ? "us" : c === "GB" ? "gb" : null;
}

// ------------------------------------------------------------------------------------------------ US sales tax
/**
 * Where a US owner finds the combined state + county + city sales-tax rate for the shop's address: every state's
 * Department of Revenue (list kept by the Federation of Tax Administrators). The owner enters ONE combined rate per
 * branch; no paid tax API is used.
 */
export const US_SALES_TAX_LOOKUP_URL = "https://taxadmin.org/fta-members/";
/** Default class names created for NEW US shops (owner can rename / change everything). */
export const US_TAX_CLASS_SERVICES = "Sales tax – services";
export const US_TAX_CLASS_PRODUCTS = "Sales tax – products";

// ------------------------------------------------------------------------------------------------ UK VAT
/**
 * UK VAT registration threshold: £90,000 taxable turnover in the last 12 months (since 1 April 2024).
 * Source: https://www.gov.uk/vat-registration/when-to-register (checked 2026-11-03). Pence.
 */
export const UK_VAT_THRESHOLD_MINOR = 9_000_000;
/** Warn at 80 % ("approaching") and 100 % ("exceeded") of the threshold (basis points of the threshold). */
export const UK_VAT_WARN_BP = { approaching: 8_000, exceeded: 10_000 } as const;
export const UK_VAT_STANDARD_PPM = 200_000;
export type VatThresholdLevel = "ok" | "approaching" | "exceeded";
export function vatThresholdLevel(turnoverMinor: number, thresholdMinor = UK_VAT_THRESHOLD_MINOR): VatThresholdLevel {
  if (thresholdMinor <= 0) return "ok";
  const bp = (turnoverMinor * 10_000) / thresholdMinor;
  return bp >= UK_VAT_WARN_BP.exceeded ? "exceeded" : bp >= UK_VAT_WARN_BP.approaching ? "approaching" : "ok";
}

// ------------------------------------------------------------------------------------------------ UK minimum wage
/**
 * National Minimum Wage / National Living Wage hourly rates (pence) by the date they apply from.
 * Source: https://www.gov.uk/national-minimum-wage-rates (checked 2026-11-03: April 2025, April 2026 and the announced
 * April 2027 rates). Bands since 1 April 2024: 21 and over (NLW), 18 to 20, under 18. The apprentice rate is not applied
 * automatically (apprenticeship status is not recorded) — the check uses the age band.
 */
export interface UkWageRates { from: string; age21: number; age18to20: number; under18: number; apprentice: number }
export const UK_MINIMUM_WAGE: UkWageRates[] = [
  { from: "2024-04-01", age21: 1144, age18to20: 860, under18: 640, apprentice: 640 },
  { from: "2025-04-01", age21: 1221, age18to20: 1000, under18: 755, apprentice: 755 },
  { from: "2026-04-01", age21: 1271, age18to20: 1085, under18: 800, apprentice: 800 },
  { from: "2027-04-01", age21: 1321, age18to20: 1127, under18: 831, apprentice: 831 },
];
export type UkWageBand = "age21" | "age18to20" | "under18";
/** Minimum hourly rate on a date for an age (null age = unknown: the 21+ rate is used and `ageKnown` is false). */
export function ukMinimumWage(onDate: string, age: number | null): { rateMinor: number; band: UkWageBand; ageKnown: boolean; from: string } | null {
  const row = [...UK_MINIMUM_WAGE].reverse().find((r) => r.from <= onDate);
  if (!row) return null;
  const band: UkWageBand = age === null || age >= 21 ? "age21" : age >= 18 ? "age18to20" : "under18";
  return { rateMinor: row[band], band, ageKnown: age !== null, from: row.from };
}

// ------------------------------------------------------------------------------------------------ UK holiday accrual
/**
 * Irregular-hours and part-year workers (leave years from 1 April 2024): holiday accrues at 12.07 % of the hours worked
 * in each pay period; rolled-up holiday pay may instead be paid at 12.07 % of the pay for the period.
 * Source: https://www.gov.uk/guidance/holiday-pay-and-entitlement-reforms-from-1-january-2024
 */
export const UK_HOLIDAY_ACCRUAL_BP = 1_207;
export type HolidayAccrualMode = "off" | "hours" | "rolled_up";
/** Holiday hours accrued (in minutes) for minutes worked. */
export const holidayAccrualMinutes = (workedMinutes: number) => Math.round((Math.max(0, workedMinutes) * UK_HOLIDAY_ACCRUAL_BP) / 10_000);
/** Rolled-up holiday pay for the pay of the period. */
export const rolledUpHolidayPay = (payMinor: number) => Math.round((Math.max(0, payMinor) * UK_HOLIDAY_ACCRUAL_BP) / 10_000);
